import type { RecordModel } from 'pocketbase';
import type { Capabilities } from '@kioskforge/shared';
import { pb } from './pocketbase';
import { apiRequest } from './client';
import { realtimeSubscribe } from './realtime';

export type DeviceRecord = RecordModel & {
  name?: string;
  group?: string;
  status?: string;
  last_seen?: string;
  app_version?: string;
  ip?: string;
  battery?: number;
  capabilities?: Capabilities;
  config_version?: number;
  current_deployment?: string;
};

export type DeviceCommandType = 'reload' | 'clear_cache' | 'update_config' | 'restart' | 'rotate_token';
export type ClaimResult = { device_id: string; name: string; token_shown_once: boolean };
export type CommandResult = { command_id: string; type: string; expires_at: string };

export async function listDevices(): Promise<DeviceRecord[]> {
  const records = await pb.collection('devices').getFullList({ sort: '-last_seen' });
  return records as DeviceRecord[];
}

/** Realtime: re-resolve the tenant's device list whenever a row changes. */
export function subscribeDevices(onChange: (devices: DeviceRecord[]) => void): () => void {
  let active = true;
  const stop = realtimeSubscribe('devices', () => {
    void listDevices().then((devices) => {
      if (active) onChange(devices);
    });
  });
  return () => {
    active = false;
    stop();
  };
}

/** Rename/regroup are permitted by the devices update rule (non-locked fields). */
export async function renameDevice(id: string, name: string): Promise<void> {
  await pb.collection('devices').update(id, { name });
}

export async function regroupDevice(id: string, group: string): Promise<void> {
  await pb.collection('devices').update(id, { group });
}

/** Revoke goes through the server (it rotates the token hash and invalidates the cache). */
export async function revokeDevice(id: string): Promise<void> {
  await apiRequest(`/devices/${id}/revoke`, { method: 'POST' });
}

export async function sendCommand(id: string, type: DeviceCommandType): Promise<CommandResult> {
  return apiRequest<CommandResult>(`/devices/${id}/commands`, { method: 'POST', body: { type } });
}

export async function claimDevice(code: string, name: string, group?: string): Promise<ClaimResult> {
  const result = await apiRequest<ClaimResult>('/devices/claim', { method: 'POST', body: { code, name } });
  if (group) await regroupDevice(result.device_id, group);
  return result;
}
